import store from './store';
import router from './routes';
import Axios from 'axios';
import qs from 'qs';
import response from '@components/Partials/Response.vue';

const axios = Axios.create({
    baseURL: process.env.MIX_API_BASE_URL,
    timeout: 20000,
    transformRequest: (data, headers) => {
        //Add jwt auth header
        if(store.state.auth.token) {
            headers.common.Authorization = `Bearer ${ store.state.auth.token }`;
        }

        if(data) {
            data = qs.stringify(data);
            headers.common['Content-Type'] = 'application/x-www-form-urlencoded';
        }

        headers.common['X-Requested-With'] = 'XMLHttpRequest';

        return data;
    },
});

//Update jwt from response header
axios.interceptors.response.use(response => {
    api.updateToken(response);
    return response;
}, err => {
    if(!err.response) {
        api.updateToken(err.response);
        return Promise.reject(err);
    }

    if(!err.response.status) {
        api.updateToken(err.response);
        return Promise.reject(err);
    }

    if(err.response.status === 401 && store.getters.loggedIn) {
        console.warn(`Token invalid: ${ store.state.auth.token }`);

        store.dispatch('logout');
        window.location.reload(true);

        return Promise.reject(err);
    }

    if(err.response.status === 403 && store.getters.loggedIn) {
        console.warn('User suspended');

        store.dispatch('logout');
        window.location.reload(true);

        return Promise.reject(err);
    }

    api.updateToken(err.response);
    return Promise.reject(err);
});

const api = {
    /**
     * Login using username/password
     * @param username - Email address
     * @param password - Password
     * @returns Promise
     */
    login(username, password) {
        return axios.post(`auth/login`, {
            username: username,
            password: password,
        });
    },

    forgotPassword(email) {
        return axios.post('auth/forgot', {
            email: email,
        });
    },

    resetPassword(token, email, password) {
        return axios.post('auth/reset', {
            token: token,
            email: email,
            password: password,
        });
    },

    getDashboardCount() {
        return axios.get('new').then(response => {
            return response.data;
        });
    },

    search(query) {
        return axios.get('search', {
            params: { query },
        }).then(response => {
            // console.log({ test: response.data });
            return response.data;
        });
    },

    searchRelatedItems(query, sectorId) {
        return axios(`related-items?query=${ encodeURIComponent(query) }&sectors[]=${ sectorId }`)
            .then(response => response.data);
    },

    getAttachment(id) {
        return axios.get(`dropzone/preview/${ id }`)
            .then(response => response.data.data);
    },

    getAttachments(ids) {
        if(!ids || typeof ids !== 'object' || !ids.length) {
            return false;
        }

        const reqs = ids.map((id) => axios.get(`dropzone/preview/${ id }`));

        return Promise.all(reqs).then((response) => {
            return response.map((innerResponse) => innerResponse.data.data)
        })
    },

    deleteAttachment(id) {
        return axios.delete(
            `attachments/dropzone/${ id }`,
            {baseURL: process.env.APP_URL},
        )
            .then(response => response.data);
    },

    //Users
    getProfile() {
        return axios.get('profile').then(response => {
            return response.data.data || [];
        });
    },

    updateProfile() {
        return axios.patch('profile');
    },

    getUsers(params) {
        return axios.get(`users`, {
            params: params,
        }).then(response => {
            return response.data || [];
        });
    },

    searchUsers(params) {
        return axios.get(`users/search`, { params })
            .then(response => {
                return response.data || [];
            });
    },

    getUser(id) {
        return axios.get(`users/${ id }`).then(response => {
            return response.data.data || [];
        });
    },

    createUser(data) {
        return axios.post(`users`, data);
    },

    importUsers(data) {
        return axios.post(`users/import`, data).then(response => {
            return response.data.data || [];
        });
    },

    updateUser(id, data) {
        return axios.patch(`users/${ id }`, data);
    },

    updateUserPassword(id, data) {
        return axios.patch(`users/${ id }/password`, data);
    },

    updateUserSpecialisms(id, specialisms) {
        return axios.patch(`users/${ id }/specialisms`, {specialisms});
    },

    updateUserSubscribed(id, subscribed) {
        return axios.patch(`users/${ id }/subscribed`, {subscribed});
    },

    suspendUser(id) {
        return axios.post(`users/${ id }/suspend`);
    },

    unsuspendUser(id) {
        return axios.post(`users/${ id }/unsuspend`);
    },

    impersonateUser(id) {
        return axios.post(`auth/impersonate/${ id }`);
    },

    leaveImpersonation() {
        return axios.post(`auth/revert`);
    },

    //Members
    getMembers(params) {
        return axios.get(`members`, {
            params: params,
        }).then(response => {
            return response.data.data || [];
        });
    },

    getEvents(params) {
        return axios.get(`events`, {
            params: params,
        }).then(response => {
            return response.data.data || [];
        });
    },

    searchEvents(params) {
        return axios.get(`events/search`, { params })
            .then(response => response.data.data || []);
    },

    getMember(id) {
        return axios.get(`members/${ id }`).then(response => {
            return response.data.data || [];
        });
    },

    createMember(formData) {
        return axios.post('members', formData).then(response => {
            return response.data.data;
        });
    },

    updateMember(id, step, formData) {
        return axios.patch(`members/${ id }`, {
            step,
            ...formData,
        }).then(response => {
            return response.data.data;
        });
    },

    //Opportunities
    getOpportunities(params) {
        return axios.get(`opportunities`, {
            params: params,
        }).then(response => {
            return response.data || {data: []};
        });
    },

    searchOpportunities(params) {
        return axios
            .get(`opportunities/search`, {
                params: params,
            })
            .then(response => (response.data || { data: [] }));
    },

    getOpportunity(id) {
        return axios.get('opportunities/' + id).then(response => {
            return response.data.data || [];
        });
    },

    createMealRegistrations(id) {
        return axios.post('meal-registrations', {
            id,
        }).then(response => {
            console.log('Meal Registration with: ', response);
            return response.data.data;
        });
    },

    setOpportunityResponse(opportunity_id, formData) {
        return axios.post('opportunities/' + opportunity_id + '/responses', formData).then(response => {
            return response.data.data || [];
        });
    },

    setOpportunityFeedback(opportunity_id, formData) {
        return axios.put('opportunities/' + opportunity_id + '/feedback', formData).then(response => {
            return response.data.data || [];
        });
    },

    updateOpportunityResponse(opportunity_id, response_id, formData) {
        return axios.patch('opportunities/' + opportunity_id + '/responses/' + response_id, formData).then(response => {
            return response.data.data || [];
        });
    },

    deleteOpportunityResponse(opportunity_id, response_id) {
        return axios.delete('opportunities/' + opportunity_id + '/responses/' + response_id).then(response => {
            return response.data.data || [];
        });
    },


    createOpportunity(formData) {
        return axios.post('opportunities', formData).then(response => {
            return response.data.data;
        });
    },

    updateOpportunity(id, step, formData) {
        return axios.patch(`opportunities/${ id }`, {
            step,
            ...formData,
        }).then(response => {
            return response.data.data;
        });
    },

    deleteOpportunity(id) {
        return axios.delete(`opportunities/${ id }`);
    },

    shareOpportunity(id, members) {
        return axios.post(`opportunities/${ id }/share`, {
            members,
        });
    },

    //MADDs
    getMADDs(params) {
        return axios.get('madds', {params})
            .then(response => (response.data || { data: []}));
    },
    //MADDs
    searchMADDs(params) {
        return axios.get('madds/search', {params})
            .then(response => (response.data || { data: []}));
    },

    getMADD(id) {
        return axios.get('madds/' + id).then(response => {
            return response.data.data || [];
        });
    },

    getDocument(eventId, id) {
        return axios.get(`event/${ eventId }/documents/${ id }`)
            .then(res => res.data.data || []);
    },

    deleteDocument(id) {
        return axios.delete('event-document/' + id);
    },

    getDocumentForMember(memberId, eventId) {
        console.log('Getting documents for members')
        return axios.post('event-document/exists', {
            eventId: eventId,
            memberId: memberId,
        }).then((res) => res.data.data || [])
    },

    submitDocument(id) {
        return axios.post(`event-document/${ id }/submit`).then(res => res.data.data || []);
    },

    createMADD(formData) {
        return axios.post('madds', formData).then(response => {
            return response.data.data;
        });
    },

    updateMADD(id, step, formData) {
        return axios.patch(`madds/${ id }`, {
            step,
            ...formData,
        }).then(response => {
            return response.data.data;
        }).catch(e => {
            console.log(e);
        })
    },

    deleteMADD(id) {
        return axios.delete(`madds/${ id }`);
    },

    deleteActivity(id) {
        return axios.delete(`activity/${ id }`);
    },

    //Intelligence
    getIntelligenceList(params) {
        return axios.get('intelligence', {params}).then(response => {
            return response.data || {data: []};
        });
    },

    getIntelligence(id) {
        return axios.get('intelligence/' + id).then(response => {
            return response.data.data || [];
        }).catch(error => {
            console.error(error);

            return error;
        });
    },

    createIntelligence(formData) {
        return axios.post('intelligence', formData).then(response => {
            return response.data.data;
        });
    },

    updateIntelligence(id, formData) {
        return axios.patch(`intelligence/${ id }`, formData).then(response => {
            return response.data.data;
        });
    },

    createActivity(formData) {
        return axios.post('activity', formData).then(response => {
            console.log('Creating Activity with: ', response);
            return response.data.data;
        });
    },

    updateActivity(id, formData) {
        return axios.patch(`activity`, formData).then(response => {
            console.log('Updating Activity with: ', response);
            return response.data.data;
        });
    },

    createEvent(formData) {
        return axios.post('event', formData).then(response => {
            console.log('Creating Event with: ', response);
            return response.data.data;
        });
    },

    updateEvent(id, formData) {
        return axios.patch(`event`, formData).then(response => {
            console.log('Updating Event with: ', response);
            return response.data.data;
        });
    },

    getGeoLocation(location) {
        return axios.post(`geolocation`, {
            location,
        }).then(response => {
            return response.data.data;
        });
    },

    deleteIntelligence(id) {
        return axios.delete(`intelligence/${ id }`);
    },

    //Insights
    getInsights(params) {
        return axios.get('insights', {params}).then(response => {
            return response.data || {data: []};
        });
    },

    //Insights
    searchInsights(params) {
        return axios.get('insights/search', {params})
            .then(response => (response.data || {data: []}));
    },

    getInsightCategories() {
        return axios.get('insight-categories').then(response => {
            return response.data;
        });
    },

    getTopLevelInsightCategories() {
        return axios.get('insight-categories/top-level').then(response => {
            return response.data.data;
        });
    },

    getInsightCategory(categoryID) {
        return axios
            .get(`insight-categories/${categoryID}`)
            .then(response => response.data.data);
    },

    createInsightCategory(formData) {
        return axios.post('insight-categories', formData).then(response => {
            return response.data.data;
        });
    },

    updateInsightCategory(categoryID, formData) {
        return axios
            .put(`insight-categories/${categoryID}`, formData)
            .then(response => response.data.data);
    },

    deleteInsightCategory(category, formData) {
        return axios.delete(`insight-categories/${ category }`, { data: formData });
    },

    createInsight(formData) {
        return axios.post('insights', formData).then(response => {
            return response.data.data;
        });
    },

    updateInsight(id, step, formData) {
        return axios.patch(`insights/${ id }`, {
            step,
            ...formData,
        }).then(response => {
            return response.data.data;
        });
    },

    deleteInsight(id) {
        return axios.delete(`insights/${ id }`);
    },

    getInsight(id) {
        return axios.get(`insights/${ id }`).then(response => {
            return response.data.data || [];
        }).catch(error => {
            console.error(error);

            return error;
        });
    },

    //Sectors
    getSectors(params = {}) {
        return axios.get('sectors', {
            params,
            timeout: 20000,
        }).then(response => {
            return response.data.data;
        });
    },

    getSector(id) {
        return axios.get('sectors/' + id).then(response => {
            return response.data.data;
        })
    },

    getSubSectors(id) {
        return axios.get(`sectors/${ id }/sub-sectors`).then(response => {
            return response.data.data;
        })
    },

    getSectorCounts(id) {
        return axios.get(`sectors/${ id }/counts`).then(response => {
            return response.data;
        })
    },

    updateSector(id, formData) {
        return axios.patch(`sectors/${ id }`, formData).then(response => {
            return response.data.data;
        });
    },

    createSector(formData) {
        return axios.post('sectors', formData).then(response => {
            return response.data.data;
        });
    },

    createDocument(data) {
        return axios.post('event-document', data).then(response => {
            return response.data
        });
    },

    editDocument(data, id) {
        return axios.patch(`event-document/${ id }`, data).then(response => {
            return response.data;
        });
    },

    createReport(data) {
        return axios.post('reports', data).then(response => {
            return response.data.data;
        });
    },

    downloadAttachment(uuid, name) {
        axios.defaults.timeout = 60000;
        return axios.get(`download/${ uuid }`, {
            responseType: 'blob',
        })
            .then(blob => {
                let a = document.createElement('a');
                a.href = window.URL.createObjectURL(blob.data);
                a.download = name;
                a.hidden = true;

                document.body.appendChild(a);
                a.click();

                window.URL.revokeObjectURL(a.href);
                a.remove();
            });
    },

    updateToken(response) {
        if(response.headers.authorization) {
            let token = response.headers.authorization.replace('Bearer ', '');
            store.commit('setToken', token);
        }
    },

    mapServerValidation(response, validator) {
        validator.errors.removeById('server');

        if(!response || !response.data || !response.data.errors) {
            validator.errors.add({
                field: 'server',
                id: 'server',
                msg: 'An error has occurred. Please try again.',
            });

            return;
        }

        let errors = response.data.errors;

        console.log('Finding errors for ', validator.fields)

        for(let fieldName in errors) {
            if(!errors.hasOwnProperty(fieldName)) {
                continue;
            }

            const field = validator.fields.find({name: fieldName});

            errors[fieldName].forEach(msg => {
                validator.errors.add({
                    field: fieldName,
                    id: field ? field.id : 'server',
                    msg,
                });
            });

            if(field) {
                field.setFlags({
                    invalid: true,
                    valid: false,
                    validated: true,
                });
            }
        }
    },
    getEvent() {
        return axios.get('events').then(response => {
            return response.data.data;
        });
    },
    getEventById(id) {
        return axios.get(`events/${ id }`).then(response => {
            return response.data.data;
        });
    },
    getEventByIdForm(id) {
        return axios.get(`event/${id}/form`).then(response => {
           return response.data.data;
        });
    },
    getEventDocuments(id){
        return axios.get(`event/${id}/documents`).then(res => res.data.data || null);
    },
    getFullEventById(id) {
        return axios.get(`events/${ id }/full`).then(response => {
            return response.data.data;
        });
    },
    getActivity(id) {
        return axios.get(`activities/${ id }`).then(response => {
            return response.data.data;
        });
    },
    addRegistration(data) {
        return axios.post('registration', data).then(response => {
            return response.data;
        });
    },
    isUserSubscribed(eventId) {
        return axios.post('registration/is-registered', {
            event_id: eventId,
        }).then(response => {
            return response.data;
        });
    },

    getSectorFeed(params) {
        return axios.get(`sector-post`, { params, timeout: 20000 })
            .then(response => response.data);
    },

    getSectorLeagueTable(sectorId) {
        return axios.get(`sectors/${sectorId}/table`)
            .then(response => response.data.data);
    },

    getSectorPost(id) {
        return axios.get('sector-post/' + id).then(response => {
            return response.data.data;
        })
    },

    searchSectorPosts(params) {
        return axios.get('sector-post/search', { params })
            .then(response => (response.data || { data: [] }))
    },

    saveSectorPost(data) {
        return axios.post('sector-post', data).then(response => {
            return response.data.data;
        });
    },

    updateSectorPost(postId, data) {
        return axios.put(`sector-post/${ postId }`, data).then(responser => {
            return response.data.data;
        });
    },

    deleteSectorPost(postId) {
        return axios.delete(`sector-post/${ postId }`).then(response => {
            return response.data;
        });
    },

    likeSectorPost(id) {
        return axios.post(`sector-post/${ id }/like`, {}).then(response => {
            return response.data.data;
        })
    },

    unlikeSectorPost(id) {
        return axios.delete(`sector-post/${ id }/unlike`, {}).then(response => {
            return response.data.data;
        })
    },

    getSectorPostComments(postId) {
        return axios.get(`sector-post/${ postId }/comments`).then(response => {
            return response.data.data;
        });
    },

    addSectorPostComment(postId, data) {
        return axios.post(`sector-post/${ postId }/comments`, data).then(response => {
            return response.data;
        });
    },

    deleteSectorPostComment(postId, commentId) {
        return axios.delete(`sector-post/${ postId }/comments/${ commentId }`).then(response => {
            return response.data;
        });
    },

    getNotificationCount() {
        return axios.get(`notifications/count`).then(response => {
            return response.data;
        });
    },

    getNotifications(params = {}) {
        return axios.get(`notifications`, { params }).then(response => {
            return response.data.data;
        });
    },

    clearNotification(notificationId) {
        return axios.delete(`notifications/${notificationId}`);
    },

    clearAllNotifications() {
        return axios.delete(`notifications/all`);
    },

    readNotification(notificationId) {
        return axios.get(`notifications/${notificationId}/read`).then(response => {
            return response.data;
        });
    },

    readAllNotifications() {
        return axios.get(`notifications/all/read`).then(response => {
            return response.data;
        });
    },

    getSectorTree() {
        return axios.get('sector-tree').then(response => {
            return response.data;
        });
    },


    getSynonyms(searchTerm){
        return axios.get(`search/synonyms?query=${searchTerm}`)
            .then(response => response.data);
    }
};

export {
    axios,
    api as default,
};
